import { Component, OnInit, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { SharedService } from '@myproject/shared/shared.service';
import { environment } from '@myenv/environment';
import { configMessage } from '@shared/config-message';
import { DatePipe } from '@angular/common';
import { SecondsToHmsPipe } from '@myproject/shared/seconds-to-hms.pipe';
import { OtaService } from '@myproject/components/ota/ota.service';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';

@Component({
    selector: 'app-device-activity',
    templateUrl: './device-activity.component.html',
    styleUrls: ['./device-activity.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DeviceActivityComponent implements OnInit, OnChanges {
  public dateFormat = environment.smallDateFormat;
  public isoDateFormat = environment.isoDateTimeFormat;
  failStatus = configMessage.campaignStageFailStatus;
  @Input() campaignId: string;
  @Input() campaignName: string;
  @Input() showModal: boolean;
  @Input() hideModal: boolean;
  @Input() device: any = {};
  deviceHistory: any[] = [];
  deviceHistoryView: any[] = [];
  isLoading = false;
  loadmore = false;
  pager = {
    page: 1,
    startIndex: 0,
    endIndex: 99,
    numberOfItems: 100,
    totalItems: 0
  };

  constructor(
    private sharedService: SharedService,
    private otaService: OtaService,
    private secondsToHms: SecondsToHmsPipe
  ) { }

  ngOnInit() {
  }

  ngOnChanges(changes) {
    // Show Modal
    if (changes.showModal && changes.showModal.previousValue !== undefined) {
      if (changes.showModal.previousValue !== changes.showModal.currentValue) {
        document.getElementById('historyModalBtn').click();
        this.deviceHistory = [];
        this.getDeviceHistory();
      }
    }
    // Hide Modal
    if (changes.hideModal && changes.hideModal.previousValue !== undefined) {
      if (changes.hideModal.previousValue !== changes.hideModal.currentValue) {
        document.getElementById('closeHistoryModal').click();
      }
    }
  }

  getDeviceHistory() {
    this.isLoading = true;
    const data = {
      campaignId: this.campaignId,
      createdAt: this.device.createdAt,
      serialNumber: this.device.serialNumber
    };
    this.otaService.getDeviceHistory(data).subscribe({
      next: res => {
        this.deviceHistory = res;
        this.pager.totalItems = this.deviceHistory.length;
        this.paginate(1);
        this.isLoading = false;
      },
      error: error => {
        this.isLoading = false;
        this.sharedService.getErrorMsg(error);
      }
    });
  }

  paginate(page: number) {
    const endIndex = page * this.pager.numberOfItems;
    if (this.pager.totalItems > 100 && endIndex <= this.pager.totalItems) {
      this.loadmore = true;
      this.pager.page = page;
      this.pager.endIndex = endIndex;
      this.deviceHistoryView = this.deviceHistory.slice(this.pager.startIndex, this.pager.endIndex - 1);
    } else {
      this.loadmore = false;
      this.deviceHistoryView = this.deviceHistory;
    }
  }

  loadMoreActivity() {
    this.pager.page++;
    this.paginate(this.pager.page);
  }

  // displayTooltip(data): Object {
  //   if (data.errorCode || data.remark) {
  //     return { display: true };
  //   } else {
  //     return { display: false };
  //   }
  // }

  // tooltipContent(data): string {
  //   const status = this.failStatus[data.errorCode] ? this.failStatus[data.errorCode] : 'NA';
  //   if (data.errorCode && data.remark) {
  //     return `Error: ${status}<br>Remark: ${data.remark}`;
  //   } else if (data.errorCode && !data.remark) {
  //     return `Error: ${status}`;
  //   } else if (!data.errorCode && data.remark) {
  //     return `Remark: ${data.remark}`;
  //   } else {
  //     return `No Data`;
  //   }
  // }

  exportActivityCSV() {
    const displayHeaders = [
      'Campaign Name', 'Serial Number', 'Schedule At',
      'Updated At', 'Completed At', 'Stage',
      'Status', 'Processed In', 'Scheduled By', 'Cancelled By'
    ];
    const dataKeys = [
      'campaignName', 'serialNumber', 'scheduleAt',
      'updatedAt', 'completedAt', 'stage',
      'status', 'totalTime', 'scheduledBy', 'cancelledBy'
    ];

    const tempHistory = this.deviceHistory.map(raw => ({ ...raw }));
    const rows = tempHistory.map(device => {
      return dataKeys.map(key => {
        if (key === 'campaignName') {
          return this.campaignName;
        }
        if (['scheduleAt', 'updatedAt', 'completedAt'].includes(key)) {
          const datePipe = new DatePipe('en-US');
          return device[key] ? datePipe.transform(device[key], this.isoDateFormat) : '';
        }
        if (key === 'totalTime' && device[key]) {
          return this.secondsToHms.transform(device[key], 'milliseconds');
        }
        if (key === 'stage' && device[key]) {
          return `Stage ${device[key]}`;
        }
        return device[key] || '';
      });
    });

    const wsData = [displayHeaders, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Activity');

    const filename = `${this.campaignName}_Device_Activity.csv`;
    const csvBuffer = XLSX.write(wb, { bookType: 'csv', type: 'array', FS: ',' });
    const blob = new Blob([csvBuffer], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, filename);
  }

  onClose() {
    this.deviceHistory = [];
    this.deviceHistoryView = [];
    this.loadmore = false;
    this.pager = {
      page: 1,
      startIndex: 0,
      endIndex: 99,
      numberOfItems: 100,
      totalItems: 0
    };
  }

  displayTooltip(data): Object {
    if (data.errorCode || data.remark) {
      return {display: true};
    } else {
      return {display: false};
    }
  }

  tooltipContent(data): string {
    const status = this.failStatus[data.errorCode] ? this.failStatus[data.errorCode] : 'NA';
    if (data.errorCode && data.remark) {
      return `Error: ${status}<br>Remark: ${data.remark}`;
    } else if (data.errorCode && !data.remark) {
      return `Error: ${status}`;
    } else if (!data.errorCode && data.remark) {
      return `Remark: ${data.remark}`;
    } else {
      return `No Data`;
    }
  }

  isRemarkTruncated(remark: string): boolean {
    if (!remark) return false;
    // Assuming average character width of 8px and container width of 90% (approximately 300px)
    // This is a rough estimation - you can adjust based on your actual container width
    const estimatedMaxLength = 35; // Adjust this value based on your container width
    return remark.length > estimatedMaxLength;
  }

  downloadRemark(remark: string): void {
    if (!remark) return;
    const blob = new Blob([remark], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `remark_${new Date().getTime()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
